/**
 * OWASP-Compliant Key Management System
 * Addresses OWASP A02: Cryptographic Failures
 */

interface SecureConfig {
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket?: string;
    messagingSenderId?: string;
    appId?: string;
  };
  algolia?: {
    appId: string;
    apiKey: string;
    indexName: string;
  };
}

class KeyManager {
  private static instance: KeyManager;
  private keys: Map<string, string> = new Map();
  private isInitialized = false;

  private constructor() {
    this.initializeSecurity();
  }

  public static getInstance(): KeyManager {
    if (!KeyManager.instance) {
      KeyManager.instance = new KeyManager();
    }
    return KeyManager.instance;
  }

  private initializeSecurity(): void {
    // Prevent key extraction through console
    if (process.env.NODE_ENV === 'production') {
      Object.defineProperty(this, 'keys', {
        enumerable: false,
        configurable: false,
        writable: false
      });
    }
  }

  /**
   * Securely store environment variables
   * Only accept properly prefixed variables to prevent accidental exposure
   */
  public initializeKeys(): void {
    if (this.isInitialized) {
      console.warn('🔒 Keys already initialized');
      return;
    }

    try {
      // Firebase configuration with validation
      const firebaseConfig = this.getFirebaseConfig();
      if (firebaseConfig) {
        this.keys.set('firebase_config', JSON.stringify(firebaseConfig));
      }

      // Algolia configuration (optional)
      const algoliaConfig = this.getAlgoliaConfig();
      if (algoliaConfig) {
        this.keys.set('algolia_config', JSON.stringify(algoliaConfig));
      }

      this.isInitialized = true;
      console.log('🔑 Secure key management initialized');
    } catch (error) {
      console.error('❌ Failed to initialize keys:', error);
      throw new Error('Key initialization failed');
    }
  }

  private getFirebaseConfig(): SecureConfig['firebase'] | null {
    const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
    const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

    // Validate required Firebase keys
    if (!apiKey || !authDomain || !projectId) {
      console.warn('⚠️ Firebase configuration incomplete');
      return null;
    }

    // Validate API key format (Firebase API keys start with specific patterns)
    if (!this.validateFirebaseApiKey(apiKey)) {
      console.error('❌ Invalid Firebase API key format');
      return null;
    }

    return {
      apiKey: this.obfuscateKey(apiKey),
      authDomain,
      projectId,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID
    };
  }

  private getAlgoliaConfig(): SecureConfig['algolia'] | null {
    const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
    const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;
    const indexName = import.meta.env.VITE_ALGOLIA_INDEX_NAME;

    if (!appId || !apiKey || !indexName) {
      return null;
    }

    return {
      appId,
      apiKey: this.obfuscateKey(apiKey),
      indexName
    };
  }

  private validateFirebaseApiKey(key: string): boolean {
    // Firebase web API keys typically start with 'AIza' and are 39 characters long
    return /^AIza[A-Za-z0-9_-]{35}$/.test(key);
  }

  /**
   * Simple obfuscation for keys (not encryption, just basic protection)
   * In production, consider using proper encryption
   */
  private obfuscateKey(key: string): string {
    if (process.env.NODE_ENV === 'development') {
      return key; // Don't obfuscate in development
    }

    // Simple XOR obfuscation
    const obfuscationKey = 'SecureApp2025';
    let result = '';
    for (let i = 0; i < key.length; i++) {
      result += String.fromCharCode(
        key.charCodeAt(i) ^ obfuscationKey.charCodeAt(i % obfuscationKey.length)
      );
    }
    return btoa(result);
  }

  private deobfuscateKey(obfuscatedKey: string): string {
    if (process.env.NODE_ENV === 'development') {
      return obfuscatedKey;
    }

    try {
      const encoded = atob(obfuscatedKey);
      const obfuscationKey = 'SecureApp2025';
      let result = '';
      for (let i = 0; i < encoded.length; i++) {
        result += String.fromCharCode(
          encoded.charCodeAt(i) ^ obfuscationKey.charCodeAt(i % obfuscationKey.length)
        );
      }
      return result;
    } catch {
      throw new Error('Failed to deobfuscate key');
    }
  }

  /**
   * Securely retrieve configuration
   */
  public getConfig<T extends keyof SecureConfig>(service: T): SecureConfig[T] | null {
    if (!this.isInitialized) {
      throw new Error('KeyManager not initialized. Call initializeKeys() first.');
    }

    const configKey = `${service}_config`;
    const configString = this.keys.get(configKey);
    
    if (!configString) {
      return null;
    }

    try {
      const config = JSON.parse(configString);
      
      // Deobfuscate keys for actual use
      if (service === 'firebase' && config.apiKey) {
        config.apiKey = this.deobfuscateKey(config.apiKey);
      }
      if (service === 'algolia' && config.apiKey) {
        config.apiKey = this.deobfuscateKey(config.apiKey);
      }

      return config;
    } catch (error) {
      console.error(`❌ Failed to parse ${service} config:`, error);
      return null;
    }
  }

  /**
   * Security audit - check for exposed keys
   */
  public auditSecurity(): { passed: boolean; issues: string[] } {
    const issues: string[] = [];

    // Check if keys are properly configured
    if (!this.isInitialized) {
      issues.push('Key manager not initialized');
    }

    // Check for Firebase config
    const firebaseConfig = this.keys.get('firebase_config');
    if (!firebaseConfig) {
      issues.push('Firebase configuration missing');
    }

    // Check environment
    if (process.env.NODE_ENV === 'production') {
      // In production, ensure no dev-only configurations
      if (import.meta.env.DEV) {
        issues.push('Development mode detected in production build');
      }
    }

    // Check for common security misconfigurations
    if (typeof window !== 'undefined') {
      // Check if keys are exposed on window object
      const windowKeys = Object.keys(window).filter(key => 
        key.toLowerCase().includes('key') || 
        key.toLowerCase().includes('secret') ||
        key.toLowerCase().includes('token')
      );
      
      if (windowKeys.length > 0) {
        issues.push(`Potential key exposure on window object: ${windowKeys.join(', ')}`);
      }
    }

    return {
      passed: issues.length === 0,
      issues
    };
  }

  /**
   * Clear all keys from memory (for logout/cleanup)
   */
  public clearKeys(): void {
    this.keys.clear();
    this.isInitialized = false;
    console.log('🔒 Keys cleared from memory');
  }
}

// Export singleton instance
export const keyManager = KeyManager.getInstance();

// Auto-initialize keys
if (typeof window !== 'undefined') {
  try {
    keyManager.initializeKeys();
  } catch {
    console.warn('⚠️ Key initialization delayed due to missing environment variables');
  }
}

export default keyManager;
